import * as React from 'react';
import { Clock } from 'lucide-react';
import type {
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  SupplierApiError,
  type AdminContactDto,
  type AdminEstablishmentDto,
  type ContactTokenAccessDto,
  type EnrichmentRefreshResult,
  type EnrichmentSnapshotDto,
  type TokenActivationStatusDto,
} from '@/api/supplier-client';
import {
  useAddContact,
  useArchiveEstablishment,
  useReactivateEstablishment,
  useRefreshEnrichment,
  useRefuseEstablishment,
  useResendContactActivation,
  useRevokeContact,
  useSendInvitation,
  useSupplierContactsTokenAccess,
  useSupplierContactsTokenStatus,
  useSupplierDetail,
  useSuspendEstablishment,
  useTransferMaster,
} from '@/api/use-suppliers';
import {
  resendAffordance,
  resendFailureCode,
  resendFailureMessage,
} from '@/api/resend-activation-error';
import { useCguSignedStatus, useRelanceCgu } from '@/api/use-legal-admin';
import {
  useSupplierOpenCampaignFlag,
  useSupplierParticipationsBySiret,
} from '@/api/use-participations';
import type {
  SupplierCampaignParticipationDto,
  SupplierOpenCampaignFlagDto,
} from '@/api/participations-client';
import {
  campaignStateLabel,
  cycleNumberLabel,
  participationStateLabel,
} from '@/lib/campaign-labels';
import {
  LegalAdminApiError,
  type AdminSignedStatusDto,
} from '@/api/legal-admin-client';
import { CguSignatureCell } from '@/routes/admin/cgu/cgu-signature-status';
import { ForceResignatureAction } from '@/routes/admin/cgu/force-resignature-action';
import { ForcedResignatureJournalSection } from '@/routes/admin/cgu/forced-resignature-journal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import {
  contactRoleLabel,
  contactStateLabel,
  enrichmentDegradedReasonLabel,
  establishmentStateLabel,
  provenanceLabel,
} from '@/lib/supplier-labels';

// =============================================================================
// supplier-detail-screen.tsx — Écran A-04 « Fiche fournisseur » (Admin).
// =============================================================================
// GET /admin/establishments/:siret (read-model D5).
//
// Sections :
//   1. Dossier EnrichmentSnapshot socle DataGouv (raisonSociale/codeNaf/adresse +
//      provenance + as-of). dossier === null → MODE DÉGRADÉ fail-open : message
//      « enrichissement indisponible, saisie manuelle » (cohérent ACL D5).
//   2. Vigilance BODACC (niveaux 2/3) = MARQUEUR DIFFÉRÉ S6.
//   3. Interlocuteurs Contact[] (rôle + état rendus en LIBELLÉ). MASTER « Invité »
//      → message explicite « ce SIRET ne peut pas encore opérer » (l'état dit la
//      vérité, Option A). Actions contacts : révoquer / transférer MASTER.
//   4. Participations = MARQUEUR DIFFÉRÉ (BC campaign ABSENT S5).
//   5. Commandes admin : invitation (acte 2), transitions d'état, refresh.
//      Confirmation pour les DESTRUCTIVES (suspend/archive/revoke/transfer).
//
// WCAG 2.1 AA : <h1> unique, sections nommées (aria-labelledby), badges doublés
// d'un libellé, ConfirmDialog accessible (focus, Échap).

export interface SupplierDetailScreenProps {
  readonly siret: string;
  /** Retour vers la liste A-03 (navigation portée par l'adaptateur routeur). */
  readonly onBack: () => void;
}

export function SupplierDetailScreen({
  siret,
  onBack,
}: SupplierDetailScreenProps): React.JSX.Element {
  const detail = useSupplierDetail(siret);

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Fiche fournisseur
          </h1>
          <p className="font-mono text-muted-foreground">{siret}</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          Retour à la liste
        </Button>
      </header>

      {detail.isPending ? (
        <p role="status" className="text-muted-foreground">
          Chargement de la fiche…
        </p>
      ) : detail.isError ? (
        <p role="alert" className="text-destructive">
          Erreur de chargement : {detail.error.message}
        </p>
      ) : (
        <DetailBody establishment={detail.data} siret={siret} />
      )}
    </div>
  );
}

interface DetailBodyProps {
  readonly establishment: AdminEstablishmentDto;
  readonly siret: string;
}

function DetailBody({
  establishment,
  siret,
}: DetailBodyProps): React.JSX.Element {
  const state = establishmentStateLabel(establishment.etablissementState);
  // Statut de signature CGU (endpoint admin legal cross-fournisseur, S5bis). Mergé PAR
  // SIRET — composition front, le read-model supplier-reference n'est PAS modifié (isolation BC).
  const signedStatus = useCguSignedStatus();
  return (
    <div className="space-y-8">
      <section
        aria-labelledby="identity-heading"
        className="space-y-2"
      >
        <h2 id="identity-heading" className="text-lg font-medium">
          Identité
        </h2>
        <Card>
          <CardContent className="grid gap-4 py-6 sm:grid-cols-2">
            <Field label="SIREN" value={establishment.siren} mono />
            <Field
              label="Raison sociale légale"
              value={establishment.raisonSocialeLegale ?? 'Non renseignée'}
            />
            <Field label="SIRET" value={establishment.siret} mono />
            <div>
              <p className="text-xs font-medium text-muted-foreground">État</p>
              <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Signature CGU
              </p>
              <CguSignatureCell status={signedStatus.data?.[siret]} />
              <CguRelanceAction siret={siret} status={signedStatus.data?.[siret]} />
              {/* R15-GOV-04 — action admin ciblée « Forcer la resignature » (motif obligatoire). Après
                  succès, les contacts Actifs passent ResignatureRequise : le badge d'état de contact
                  correspondant est déjà rendu dans la section Interlocuteurs (invalidation de la fiche). */}
              <div className="mt-2">
                <ForceResignatureAction siret={siret} />
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* R15-GOV-04 — encart de RELECTURE admin du journal des re-signatures forcées (motif EN CLAIR,
          champ de conformité admin-relisible, DBU §2.9 l.2136-2144). Se recharge après un forçage
          (invalidation de la query journal dans useForceResignatureCgu). */}
      <ForcedResignatureJournalSection siret={siret} />

      <DossierSection dossier={establishment.dossier} siret={siret} />

      <InterlocuteursSection
        contacts={establishment.interlocuteurs}
        siret={siret}
      />

      <AccessTokensSection
        contacts={establishment.interlocuteurs}
        siret={siret}
      />

      <ParticipationsSection siret={siret} />

      <CommandsSection
        siret={siret}
        state={establishment.etablissementState}
      />
    </div>
  );
}

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

function Field({ label, value, mono = false }: FieldProps): React.JSX.Element {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className={mono ? 'font-mono' : undefined}>{value}</p>
    </div>
  );
}

// --- Action « Relancer la signature CGU » (T5 — REF 1.7 §5) ---
//
// CONDITIONNELLE : rendue UNIQUEMENT pour un NON-signataire de la version courante
// (`status.hasSigned === false` — le SIRET a signé une version antérieure et doit re-signer).
// Un SIRET absent de la map (jamais signataire, status `undefined`) ⇒ pas de bouton : parité stricte
// avec l'état de signature déjà affiché par `CguSignatureCell` (qui ne rend « À re-signer » que dans
// ce même cas). BEST-EFFORT : le clic déclenche la relance (event → worker email) et un feedback inline.
// Non destructive ⇒ pas de ConfirmDialog (parité `ContactResendAction` « Renvoyer le lien », pas des
// actions destructives). WCAG : aria-label explicite, désactivation pendant l'appel, feedback role live.

/** Traduit une erreur de relance en message utilisateur PAR CODE (jamais le message serveur brut). */
function relanceErrorMessage(error: unknown): string {
  if (error instanceof LegalAdminApiError && error.status === 409) {
    return 'Ce fournisseur a déjà signé la version en vigueur.';
  }
  return "La relance n'a pas pu être envoyée. Réessayez plus tard.";
}

function CguRelanceAction({
  siret,
  status,
}: {
  readonly siret: string;
  readonly status: AdminSignedStatusDto | undefined;
}): React.JSX.Element | null {
  const relance = useRelanceCgu();
  const [pending, setPending] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{
    tone: 'success' | 'error';
    message: string;
  } | null>(null);

  // Bouton conditionnel : non-signataire de la version courante uniquement.
  if (status === undefined || status.hasSigned) {
    return null;
  }

  const onRelance = (): void => {
    setFeedback(null);
    setPending(true);
    relance.mutate(siret, {
      onSuccess: () => {
        setFeedback({ tone: 'success', message: 'Relance envoyée.' });
      },
      onError: (error) => {
        setFeedback({ tone: 'error', message: relanceErrorMessage(error) });
      },
      onSettled: () => {
        setPending(false);
      },
    });
  };

  return (
    <div className="mt-2 flex flex-col items-start gap-1">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={pending}
        aria-label={`Relancer la signature CGU de ${siret}`}
        onClick={onRelance}
      >
        {pending ? 'Envoi…' : 'Relancer la signature CGU'}
      </Button>
      {feedback !== null ? (
        <p
          role={feedback.tone === 'error' ? 'alert' : 'status'}
          className={
            feedback.tone === 'error'
              ? 'text-xs text-destructive'
              : 'text-xs text-muted-foreground'
          }
        >
          {feedback.message}
        </p>
      ) : null}
    </div>
  );
}

// --- Section 1/2 — Dossier d'enrichissement (socle DataGouv + BODACC différé) ---

interface DossierSectionProps {
  readonly dossier: EnrichmentSnapshotDto | null;
  readonly siret: string;
}

function DossierSection({
  dossier,
  siret,
}: DossierSectionProps): React.JSX.Element {
  const refresh = useRefreshEnrichment();
  return (
    <section aria-labelledby="dossier-heading" className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h2 id="dossier-heading" className="text-lg font-medium">
          Dossier d'enrichissement
        </h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => refresh.mutate(siret)}
          disabled={refresh.isPending}
        >
          {refresh.isPending ? 'Actualisation…' : "Actualiser l'enrichissement"}
        </Button>
      </div>

      {/* Résultat du dernier refresh on-demand (union fail-open ok | degraded). */}
      {refresh.data !== undefined ? (
        <RefreshOutcome result={refresh.data} />
      ) : null}

      {dossier === null ? (
        // MODE DÉGRADÉ fail-open (dossier null) — cohérent ACL D5.
        <Card>
          <CardContent className="py-6">
            <p role="status" className="text-sm">
              Enrichissement indisponible : saisie manuelle requise.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Les données légales (raison sociale, code NAF, adresse de siège)
              n'ont pas pu être récupérées auprès de DataGouv.
            </p>
          </CardContent>
        </Card>
      ) : (
        <SnapshotCard snapshot={dossier} title="Socle DataGouv" />
      )}

      {/* MARQUEUR DIFFÉRÉ S6 : vigilance BODACC (niveaux 2/3). */}
      <Card>
        <CardContent className="py-6">
          <p className="text-sm font-medium">Vigilance BODACC</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Annonces, procédures collectives et modifications statutaires (niveaux
            2/3) seront affichées ici. Intégration BODACC prévue en S6.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}

// --- Résultat d'un refresh on-demand (union discriminée, fail-open) ---

interface RefreshOutcomeProps {
  readonly result: EnrichmentRefreshResult;
}

/**
 * Rend le résultat du dernier refresh d'enrichissement. Union discriminée sur `status` : le
 * compilateur impose de traiter les DEUX branches (aucune supposition de snapshot toujours défini).
 * UX A-04 : en mode `degraded`, l'AVERTISSEMENT précède TOUJOURS le snapshot périmé `stale`.
 */
function RefreshOutcome({ result }: RefreshOutcomeProps): React.JSX.Element {
  if (result.status === 'ok') {
    return (
      <p
        role="status"
        className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm"
      >
        Enrichissement actualisé : données à jour.
      </p>
    );
  }
  // Mode DÉGRADÉ : avertissement d'abord, PUIS le périmé (`stale`) s'il est connu.
  return (
    <div className="space-y-3">
      <p
        role="status"
        className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm"
      >
        <strong>Actualisation dégradée</strong> :{' '}
        {enrichmentDegradedReasonLabel(result.reason)}. Les données affichées
        peuvent être obsolètes ; la saisie manuelle reste possible.
      </p>
      {result.stale !== undefined ? (
        <SnapshotCard
          snapshot={result.stale}
          title="Dernier enrichissement connu (périmé)"
        />
      ) : null}
    </div>
  );
}

// --- Carte d'un snapshot d'enrichissement (réutilisée : dossier socle + périmé stale) ---

interface SnapshotCardProps {
  readonly snapshot: EnrichmentSnapshotDto;
  readonly title: string;
}

function SnapshotCard({ snapshot, title }: SnapshotCardProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>
          {provenanceLabel(snapshot.provenance)} · valide au {snapshot.asOf}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Raison sociale"
          value={snapshot.raisonSocialeLegale ?? 'Non renseignée'}
        />
        <Field
          label="Code NAF/APE"
          value={snapshot.codeNaf ?? 'Non renseigné'}
        />
        <div className="sm:col-span-2">
          <p className="text-xs font-medium text-muted-foreground">
            Adresse de siège
          </p>
          <p>{snapshot.adresseSiege?.ligneComplete ?? 'Non renseignée'}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Section 3 — Interlocuteurs (MASTER Invité = ne peut pas encore opérer) ---
//
// ÉCART CONTACTS-A04 (« Dernière connexion » admin) : le read-model A-04 (AdminEstablishmentView.
// interlocuteurs) NE SERT PAS `lastActivityAt` par interlocuteur (seule la LISTE A-03 porte
// `masterLastActivityAt`, marqueur différé). Ajouter cette colonne exigerait une modification du
// back (read-model) — HORS ce slice (contrainte « 0 back »). Aucune donnée n'est fabriquée : la
// colonne « Dernière connexion » n'est donc PAS rendue ici (follow-up A-REGISTRE). Sur l'écran
// FOURNISSEUR §3.7, `lastActivityAt` EST servi et affiché sous le libellé « Dernière connexion ».

// GARDE SUCCESSEUR MASTER (DBU §2.2 l.716, R22-REVOCATION-MSG) : la révocation d'un MASTER actif
// SANS successeur (aucun collaborateur promouvable) est refusée par l'API en 409. Le message affiché
// à l'admin doit être la chaîne VERBATIM de la DBU (mot pour mot) — jamais une reformulation. Le
// reste des erreurs (réseau, 500, etc.) retombe sur un message générique PAR CODE, parité
// `transitionErrorMessage`/`relanceErrorMessage` (jamais le message serveur brut).
function revokeErrorMessage(error: unknown): string {
  if (error instanceof SupplierApiError && error.status === 409) {
    return 'Ce contact est le référent MASTER actif. Désignez un successeur avant de procéder à la révocation.';
  }
  return "La révocation n'a pas pu être effectuée. Réessayez plus tard.";
}

/** Traduit une erreur de transfert du rôle MASTER en message utilisateur PAR CODE (parité `revokeErrorMessage`). */
function transferErrorMessage(error: unknown): string {
  if (error instanceof SupplierApiError && error.status === 409) {
    return "L'état des interlocuteurs a changé. Rechargez la fiche avant de réessayer.";
  }
  return "Le transfert du rôle maître n'a pas pu être effectué. Réessayez plus tard.";
}

/** Traduit une erreur d'ajout de contact (BUG2, DEC-18) en message utilisateur PAR CODE (jamais le message serveur brut). */
function addContactErrorMessage(error: unknown): string {
  if (error instanceof SupplierApiError && error.status === 422) {
    return "L'adresse e-mail n'appartient pas au domaine du fournisseur. Cochez « Forcer malgré un domaine différent » pour l'ajouter en connaissance de cause.";
  }
  // FU-1 (batch 1) — unicité email MÊME SIRET (ContactEmailConflictError → 409). Message CLAIR (parité
  // team-screen fournisseur), au lieu du repli générique. Le cas cross-tenant global (FU-2) reste un
  // arbitrage ARCH/SECOPS hors de ce lot (le 409 mappé ici vise l'unicité au sein du fournisseur).
  if (error instanceof SupplierApiError && error.status === 409) {
    return 'Cette adresse e-mail est déjà associée à un contact de ce fournisseur.';
  }
  if (error instanceof SupplierApiError && error.status === 400) {
    return 'Renseignez un e-mail valide, un nom et un prénom.';
  }
  return "Le contact n'a pas pu être ajouté. Réessayez plus tard.";
}

interface InterlocuteursSectionProps {
  readonly contacts: readonly AdminContactDto[];
  readonly siret: string;
}

function InterlocuteursSection({
  contacts,
  siret,
}: InterlocuteursSectionProps): React.JSX.Element {
  const revoke = useRevokeContact();
  const transfer = useTransferMaster();
  const add = useAddContact();
  const resend = useResendContactActivation();
  const tokenStatus = useSupplierContactsTokenStatus(siret);

  const [pendingRevoke, setPendingRevoke] =
    React.useState<AdminContactDto | null>(null);
  const [pendingTransfer, setPendingTransfer] =
    React.useState<AdminContactDto | null>(null);
  // BUG2-ADD-CONTACT (DEC-18) — dialog d'ajout d'un contact collaborateur PAR l'ADMIN. Le bouton est
  // TOUJOURS disponible (capacité admin) : c'est la fabrique de successeur manquante qui débloque le
  // transfert MASTER quand aucun contact n'est promouvable. Le contact naît « Invité » (cycle CGU).
  const [addOpen, setAddOpen] = React.useState(false);
  const [addEmail, setAddEmail] = React.useState('');
  const [addNom, setAddNom] = React.useState('');
  const [addPrenom, setAddPrenom] = React.useState('');
  const [addDomainOverride, setAddDomainOverride] = React.useState(false);
  const [addError, setAddError] = React.useState<string | null>(null);
  const closeAdd = React.useCallback((): void => {
    setAddOpen(false);
    setAddEmail('');
    setAddNom('');
    setAddPrenom('');
    setAddDomainOverride(false);
    setAddError(null);
  }, []);
  // Feedback d'échec des actions destructives contact (révocation / transfert MASTER). `role="alert"` :
  // porte, entre autres, le message VERBATIM DBU l.716 (garde successeur MASTER). Réinitialisé à
  // l'ouverture d'un nouveau dialog pour ne pas afficher une erreur périmée sur une nouvelle tentative.
  const [actionError, setActionError] = React.useState<string | null>(null);

  // Flag « campagne ouverte » (DBU §2.2 l.712) : fetch À L'OUVERTURE du dialog de transfert MASTER
  // uniquement (composition HTTP by-siret/open-campaign, invariant d'intégration n°1) — jamais au
  // montage de la fiche. Sert l'avertissement contextuel (CAUTION, ne bloque pas le transfert).
  const openCampaignFlag = useSupplierOpenCampaignFlag(siret, {
    enabled: pendingTransfer !== null,
  });

  const master = contacts.find((c) => c.role === 'MASTER');
  const masterInvited = master !== undefined && master.state === 'Invite';

  // Map contactId → statut du lien (composé côté front). `has(id)` distingue « pas encore chargé »
  // (undefined) de « aucune identité émise » (null).
  const statusById = React.useMemo(() => {
    const map = new Map<string, TokenActivationStatusDto | null>();
    if (tokenStatus.data !== undefined) {
      for (const entry of tokenStatus.data) {
        map.set(entry.contactId, entry.status);
      }
    }
    return map;
  }, [tokenStatus.data]);

  return (
    <section aria-labelledby="contacts-heading" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="contacts-heading" className="text-lg font-medium">
          Interlocuteurs
        </h2>
        {/* BUG2 (DEC-18) — capacité admin TOUJOURS disponible : ajoute un contact collaborateur sans
            session MASTER (fabrique de successeur). Débloque « Promouvoir maître » dès le contact Actif. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setAddError(null);
            setAddOpen(true);
          }}
        >
          Ajouter un contact
        </Button>
      </div>

      {masterInvited ? (
        <p
          role="status"
          className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm"
        >
          Le maître de ce fournisseur est <strong>invité</strong> : ce SIRET ne
          peut pas encore opérer (l'activation suit l'acceptation des CGU).
        </p>
      ) : null}

      {contacts.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Aucun interlocuteur enregistré.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Interlocuteurs du fournisseur (nom, e-mail, rôle, état, statut du lien)
            </caption>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                <th scope="col" className="px-4 py-3 font-medium">
                  Nom
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  E-mail
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Rôle
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  État
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Statut du lien
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => {
                const cstate = contactStateLabel(contact.state);
                const known = statusById.has(contact.contactId);
                const linkStatus = known
                  ? (statusById.get(contact.contactId) ?? null)
                  : undefined;
                return (
                  <tr
                    key={contact.contactId}
                    className="border-b border-border last:border-b-0"
                  >
                    {/* Nom complet (M2) — SERVI par le read-model A-04 (« nom prénom »). */}
                    <td className="px-4 py-3 align-top">{contact.nomComplet}</td>
                    <td className="px-4 py-3 align-top">{contact.email}</td>
                    <td className="px-4 py-3 align-top">
                      <Badge variant="outline">
                        {contactRoleLabel(contact.role)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StatusBadge tone={cstate.tone}>{cstate.label}</StatusBadge>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <TokenStatusCell
                        status={linkStatus}
                        pending={tokenStatus.isPending}
                      />
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="flex flex-col items-end gap-2">
                        {/* Transfert MASTER : destructif → confirmation. */}
                        {contact.role === 'COLLABORATEUR' &&
                        master !== undefined ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setActionError(null);
                              setPendingTransfer(contact);
                            }}
                            disabled={transfer.isPending}
                            aria-label={`Transférer le rôle maître à ${contact.email}`}
                          >
                            Promouvoir maître
                          </Button>
                        ) : null}
                        {contact.state !== 'Inactif' ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setActionError(null);
                              setPendingRevoke(contact);
                            }}
                            aria-label={`Révoquer ${contact.email}`}
                          >
                            Révoquer
                          </Button>
                        ) : null}
                        <ContactResendAction
                          contact={contact}
                          status={linkStatus ?? null}
                          siret={siret}
                          resend={resend}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Échec d'une révocation / d'un transfert MASTER (DBU §2.2 l.716 : chaîne VERBATIM si
          MASTER actif sans successeur ; message générique PAR CODE sinon). */}
      {actionError !== null ? (
        <p role="alert" className="text-sm text-destructive">
          {actionError}
        </p>
      ) : null}

      {/* Confirmation destructive — révocation d'un contact. */}
      <ConfirmDialog
        open={pendingRevoke !== null}
        title="Révoquer cet interlocuteur ?"
        description={
          pendingRevoke !== null
            ? `L'accès de ${pendingRevoke.email} sera désactivé. Cette action est tracée.`
            : ''
        }
        confirmLabel="Révoquer"
        onConfirm={() => {
          if (pendingRevoke !== null) {
            revoke.mutate(
              {
                siret,
                body: {
                  contactId: pendingRevoke.contactId,
                  targetKind:
                    pendingRevoke.role === 'MASTER' ? 'master' : 'collaborateur',
                  reason: 'Révocation administrateur',
                },
              },
              { onError: (error) => setActionError(revokeErrorMessage(error)) },
            );
            setPendingRevoke(null);
          }
        }}
        onCancel={() => setPendingRevoke(null)}
      />

      {/* Confirmation destructive — transfert du rôle MASTER (change le maître actif). */}
      <ConfirmDialog
        open={pendingTransfer !== null}
        title="Promouvoir cet interlocuteur maître ?"
        description={
          pendingTransfer !== null
            ? `${pendingTransfer.email} deviendra le maître actif de ce SIRET et l'ancien maître repassera collaborateur. Cette action est tracée.`
            : ''
        }
        confirmLabel="Promouvoir maître"
        onConfirm={() => {
          if (pendingTransfer !== null && master !== undefined) {
            transfer.mutate(
              {
                siret,
                body: {
                  fromContactId: master.contactId,
                  toContactId: pendingTransfer.contactId,
                },
              },
              { onError: (error) => setActionError(transferErrorMessage(error)) },
            );
            setPendingTransfer(null);
          }
        }}
        onCancel={() => setPendingTransfer(null)}
      >
        <TransferOpenCampaignWarning query={openCampaignFlag} />
      </ConfirmDialog>

      {/* BUG2-ADD-CONTACT (DEC-18) — formulaire d'ajout d'un contact collaborateur PAR l'ADMIN.
          nom/prénom/e-mail requis + dérogation domaine responsabilisée (parité enrôlement). Le
          contact naît « Invité » ; l'AUTORITÉ (mur domaine, identité, event, cycle CGU) reste l'API. */}
      <ConfirmDialog
        open={addOpen}
        title="Ajouter un contact"
        description="Ce contact collaborateur recevra un lien d'activation (72 h) et suivra le cycle Invité → Actif (acceptation des CGU). Une fois actif, il pourra être promu maître."
        confirmLabel={add.isPending ? 'Ajout…' : 'Ajouter'}
        confirmDisabled={
          add.isPending ||
          addEmail.trim() === '' ||
          addNom.trim() === '' ||
          addPrenom.trim() === ''
        }
        onConfirm={() => {
          add.mutate(
            {
              siret,
              body: {
                email: addEmail.trim(),
                nom: addNom.trim(),
                prenom: addPrenom.trim(),
                ...(addDomainOverride ? { domainOverride: true } : {}),
              },
            },
            {
              onSuccess: () => closeAdd(),
              onError: (error) => setAddError(addContactErrorMessage(error)),
            },
          );
        }}
        onCancel={closeAdd}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="add-contact-nom">Nom</Label>
            <Input
              id="add-contact-nom"
              name="addContactNom"
              autoComplete="family-name"
              value={addNom}
              onChange={(e) => setAddNom(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-contact-prenom">Prénom</Label>
            <Input
              id="add-contact-prenom"
              name="addContactPrenom"
              autoComplete="given-name"
              value={addPrenom}
              onChange={(e) => setAddPrenom(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-contact-email">E-mail</Label>
            <Input
              id="add-contact-email"
              name="addContactEmail"
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              required
            />
          </div>
          <div className="flex items-start gap-3">
            <input
              id="add-contact-domain-override"
              name="addContactDomainOverride"
              type="checkbox"
              checked={addDomainOverride}
              onChange={(e) => setAddDomainOverride(e.target.checked)}
              aria-describedby="add-contact-domain-override-help"
              className="mt-1 h-4 w-4 shrink-0 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
            <div className="space-y-1">
              <Label htmlFor="add-contact-domain-override">
                Forcer malgré un domaine différent
              </Label>
              <p
                id="add-contact-domain-override-help"
                className="text-sm text-muted-foreground"
              >
                À cocher en connaissance de cause : l'adresse n'appartient pas au
                domaine attendu du fournisseur. La dérogation est tracée.
              </p>
            </div>
          </div>
          {addError !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {addError}
            </p>
          ) : null}
        </div>
      </ConfirmDialog>
    </section>
  );
}

// --- Avertissement « campagne ouverte » au transfert MASTER (DBU §2.2 l.712, Écart B T4) ---
//
// CAUTION, PAS BLOCAGE : le transfert reste possible (variante PROTECTRICE actée — sur-avertir est sûr).
// Rendu DANS le dialog de transfert, sous la description. Trois états :
//   - fetch en cours   → statut « Vérification des campagnes en cours… » (role=status, n'empêche rien) ;
//   - campagne ouverte → bandeau d'AVERTISSEMENT (role=alert) listant les intitulés + état en LIBELLÉ ;
//   - aucune / erreur  → rien (le bouton « Promouvoir maître » reste disponible, l'API est l'autorité).
// WCAG 2.1 AA : le sens est porté par le TEXTE (« Attention » + role=alert + libellés d'état), la couleur
// n'est qu'un renfort ; contraste garanti par le style muté déjà éprouvé (parité « Actualisation dégradée »).

function TransferOpenCampaignWarning({
  query,
}: {
  readonly query: UseQueryResult<SupplierOpenCampaignFlagDto, Error>;
}): React.JSX.Element | null {
  if (query.isLoading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Vérification des campagnes en cours…
      </p>
    );
  }
  const flag = query.data;
  // Erreur ou aucune campagne ouverte → aucun avertissement (transfert non bloqué, API = autorité).
  if (flag === undefined || !flag.hasOpenCampaign) {
    return null;
  }
  const count = flag.openCampaigns.length;
  return (
    <div
      role="alert"
      className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm"
    >
      <p>
        <strong>Attention : campagne(s) en cours.</strong> Ce fournisseur
        participe à {count} campagne{count > 1 ? 's' : ''} en cours. Le transfert
        du rôle MASTER pendant un cycle ouvert peut perturber la saisie.
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        {flag.openCampaigns.map((campaign) => (
          <li key={campaign.campaignId}>
            {campaign.intitule}{' '}
            <span className="text-muted-foreground">
              ({campaignStateLabel(campaign.state).label})
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Statut du lien d'activation + renvoi (CONTACTS-A04, §2.2) ---

/**
 * Libellé FR + variant du STATUT DU LIEN d'activation (Envoyé/Expiré/Activé). Le TEXTE porte l'info ;
 * la couleur n'est qu'un renfort (WCAG). `null` (aucune identité émise) → rendu « — » par l'appelant.
 */
function tokenStatusBadge(status: TokenActivationStatusDto): {
  label: string;
  tone: StatusTone;
} {
  switch (status) {
    case 'Envoye':
      return { label: 'Envoyé', tone: 'invited' };
    case 'Expire':
      return { label: 'Expiré', tone: 'refused' };
    case 'Active':
      return { label: 'Activé', tone: 'active' };
  }
}

/** Cellule « Statut du lien » : badge texte (Envoyé/Expiré/Activé), « — » si aucune identité émise. */
function TokenStatusCell({
  status,
  pending,
}: {
  readonly status: TokenActivationStatusDto | null | undefined;
  readonly pending: boolean;
}): React.JSX.Element {
  if (status === undefined) {
    return (
      <span
        className="text-xs text-muted-foreground"
        role={pending ? 'status' : undefined}
      >
        {pending ? 'Chargement…' : '—'}
      </span>
    );
  }
  if (status === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const badge = tokenStatusBadge(status);
  return <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>;
}

/**
 * Action « Renvoyer le lien » d'un interlocuteur (Écart 1, §2.2). Affordance FRONT : actif seulement
 * si le lien est « Expiré » ; désactivé pour « Envoyé » (encore valide) ; masqué pour « Activé »/null.
 * Erreurs gérées PAR CODE (429/409/404), jamais via le message serveur brut. État LOCAL à la ligne.
 */
function ContactResendAction({
  contact,
  status,
  siret,
  resend,
}: {
  readonly contact: AdminContactDto;
  readonly status: TokenActivationStatusDto | null;
  readonly siret: string;
  readonly resend: UseMutationResult<
    void,
    Error,
    { readonly siret: string; readonly body: { readonly contactId: string } }
  >;
}): React.JSX.Element | null {
  const affordance = resendAffordance(status);
  const [pending, setPending] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{
    tone: 'success' | 'error';
    message: string;
  } | null>(null);

  if (!affordance.render) {
    return null;
  }

  const label =
    affordance.disabled && affordance.disabledReason !== null
      ? `Renvoyer le lien d'activation à ${contact.email} (${affordance.disabledReason})`
      : `Renvoyer le lien d'activation à ${contact.email}`;

  const onResend = (): void => {
    setFeedback(null);
    setPending(true);
    resend.mutate(
      { siret, body: { contactId: contact.contactId } },
      {
        onSuccess: () => {
          setFeedback({
            tone: 'success',
            message: `Lien d'activation renvoyé à ${contact.email}.`,
          });
        },
        onError: (error) => {
          setFeedback({
            tone: 'error',
            message: resendFailureMessage(resendFailureCode(error)),
          });
        },
        onSettled: () => {
          setPending(false);
        },
      },
    );
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={affordance.disabled || pending}
        aria-label={label}
        title={affordance.disabledReason ?? undefined}
        onClick={onResend}
      >
        {pending ? 'Envoi…' : 'Renvoyer le lien'}
      </Button>
      {feedback !== null ? (
        <p
          role={feedback.tone === 'error' ? 'alert' : 'status'}
          className={
            feedback.tone === 'error'
              ? 'text-xs text-destructive'
              : 'text-xs text-muted-foreground'
          }
        >
          {feedback.message}
        </p>
      ) : null}
    </div>
  );
}

// --- Section « Accès & tokens » (T6-TOKENS, §2.2) — LECTURE SEULE ---
//
// Bloc DÉTAIL des accès, DISTINCT de la colonne « Statut du lien » du tableau Interlocuteurs (vue
// « coup d'œil »). Il surface l'état EXISTANT enrichi (dates réelles du dernier token, fenêtre TTL,
// compteur/plafond de renvoi vivant) — AUCUNE écriture : le seul acte d'écriture (renvoi) reste porté
// par `ContactResendAction` dans le tableau. Une carte PAR interlocuteur ayant une identité émise
// (`status !== null`) ; les contacts sans identité sont OMIS (leur ligne « — » reste dans le tableau).
// WCAG : section nommée (aria-labelledby), statut porté par un LIBELLÉ texte, horloge « Expiré » = renfort
// DÉCORATIF (aria-hidden) toujours doublé du texte — jamais une couleur/icône seule.

/** Formate un horodatage ISO 8601 en date/heure locale FR. Chaîne brute si ISO invalide, « — » si `null`. */
function formatAccessDateTime(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}

interface AccessTokensSectionProps {
  readonly contacts: readonly AdminContactDto[];
  readonly siret: string;
}

function AccessTokensSection({
  contacts,
  siret,
}: AccessTokensSectionProps): React.JSX.Element {
  const access = useSupplierContactsTokenAccess(siret);

  // Corrélation contactId → identité (nom/e-mail) pour l'en-tête de carte (le back ne projette pas la PII
  // nominative dans la vue token-access : elle vient du read-model fiche déjà en main).
  const contactById = React.useMemo(() => {
    const map = new Map<string, AdminContactDto>();
    for (const c of contacts) {
      map.set(c.contactId, c);
    }
    return map;
  }, [contacts]);

  // Cartes : uniquement les interlocuteurs AVEC une identité émise (status !== null).
  const emitted = (access.data ?? []).filter((a) => a.status !== null);

  return (
    <section aria-labelledby="access-tokens-heading" className="space-y-4">
      <h2 id="access-tokens-heading" className="text-lg font-medium">
        Accès &amp; tokens
      </h2>

      {access.isPending ? (
        <p role="status" className="text-muted-foreground">
          Chargement des accès…
        </p>
      ) : emitted.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Aucun lien d'accès émis pour ce fournisseur.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {emitted.map((entry) => (
            <AccessTokenCard
              key={entry.contactId}
              access={entry}
              contact={contactById.get(entry.contactId)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Carte « Accès & tokens » d'un interlocuteur. `access.status` est garanti `!== null` par l'appelant.
 * Rendu selon le statut : Envoyé → « Expire le … » ; Expiré → horloge + libellé « Expiré le … » ;
 * Activé → dates atténuées + « Accès activé ». Le compteur N/plafond est servi par la vue (aucune
 * constante 72/3 codée en dur côté front). Fenêtre TTL = `ttlHours` de la politique.
 */
function AccessTokenCard({
  access,
  contact,
}: {
  readonly access: ContactTokenAccessDto;
  readonly contact: AdminContactDto | undefined;
}): React.JSX.Element {
  // `status` non-null (filtré par l'appelant) : cast local pour le badge (parité tokenStatusBadge).
  const status = access.status as TokenActivationStatusDto;
  const badge = tokenStatusBadge(status);
  const capReached = access.resendCount >= access.resendDailyCap;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base">
              {contact?.nomComplet ?? 'Interlocuteur'}
            </CardTitle>
            {contact !== undefined ? (
              <CardDescription className="truncate">
                {contact.email}
              </CardDescription>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>
            {/* Horloge « Expiré » : renfort DÉCORATIF (aria-hidden) DOUBLÉ du libellé texte (WCAG). */}
            {status === 'Expire' ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
                <Clock aria-hidden="true" className="h-3.5 w-3.5" />
                <span>Expiré</span>
              </span>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {status === 'Active' ? (
          // Accès activé : dates du dernier token ATTÉNUÉES (le compte est actif).
          <div className="space-y-1">
            <p className="font-medium">Accès activé.</p>
            {access.issuedAt !== null ? (
              <p className="text-xs text-muted-foreground">
                Lien émis le {formatAccessDateTime(access.issuedAt)}.
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Date d'envoi"
                value={formatAccessDateTime(access.issuedAt)}
              />
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  Date d'expiration
                </p>
                {status === 'Expire' ? (
                  <span className="inline-flex items-center gap-1 font-medium text-destructive">
                    <Clock aria-hidden="true" className="h-3.5 w-3.5" />
                    <span>Expiré le {formatAccessDateTime(access.expiresAt)}</span>
                  </span>
                ) : (
                  <p>Expire le {formatAccessDateTime(access.expiresAt)}</p>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Fenêtre de validité : {access.ttlHours} h après l'envoi.
            </p>
          </>
        )}

        {/* Compteur de renvoi vivant N / plafond sur 24 h (servi par la vue, jamais codé en dur). */}
        <div className="space-y-1">
          <p>
            Renvois :{' '}
            <strong>
              {access.resendCount} / {access.resendDailyCap}
            </strong>{' '}
            sur les dernières 24 h.
          </p>
          {capReached ? (
            <p role="status" className="text-xs text-destructive">
              Plafond de renvoi atteint : renvoi indisponible pour l'instant.
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

// --- Section 4 — Participations (T8 : chronologie via Be-CAMPAIGN-BY-SIRET, DBU §2.2 l.694) ---
//
// T8-PARTICIPATIONS (REF 1.7 §5) : chronologie des participations d'un SIRET aux campagnes de
// référencement. Composition UI (2ᵉ appel réseau distinct, isolation BC : supplier-reference
// n'interroge JAMAIS campaign) ; le tri « plus récente d'abord » est l'AUTORITÉ SERVEUR (aucune règle
// métier UI). LECTURE SEULE : aucune mutation, aucune confirmation. Frontières NETTES avec T4
// (endpoint `open-campaign` DISTINCT), T3 (`CommandsSection`), T5 (Identité), T6 (`AccessTokensSection`).

/**
 * Formate un horodatage ISO 8601 en date locale FR (medium, SANS heure). Chaîne brute si ISO invalide.
 * `lastRelevantAt` n'est JAMAIS null (garantie serveur) — pas de branche null (≠ formatAccessDateTime).
 */
function formatParticipationDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('fr-FR', { dateStyle: 'medium' });
}

interface ParticipationsSectionProps {
  readonly siret: string;
}

function ParticipationsSection({
  siret,
}: ParticipationsSectionProps): React.JSX.Element {
  const participations = useSupplierParticipationsBySiret(siret);

  return (
    <section aria-labelledby="participations-heading" className="space-y-4">
      <h2 id="participations-heading" className="text-lg font-medium">
        Participations aux campagnes
      </h2>
      <Card>
        <CardContent className="py-6">
          {participations.isPending ? (
            <p role="status" className="text-muted-foreground">
              Chargement des participations…
            </p>
          ) : participations.isError ? (
            <p role="alert" className="text-destructive">
              {participations.error.message}
            </p>
          ) : participations.data.participations.length === 0 ? (
            <p role="status" className="text-muted-foreground">
              Aucune participation à une campagne de référencement.
            </p>
          ) : (
            <ParticipationsTable rows={participations.data.participations} />
          )}
        </CardContent>
      </Card>
    </section>
  );
}

interface ParticipationsTableProps {
  readonly rows: readonly SupplierCampaignParticipationDto[];
}

/**
 * Tableau LECTURE SEULE de la chronologie (rendu dans l'ordre reçu = « plus récente d'abord » serveur).
 * WCAG : caption sr-only, `th scope=col`, états rendus en badge DOUBLÉ d'un libellé texte (jamais une
 * couleur seule). Clé de ligne = `participationId` (unicité garantie même dans l'hypothèse théorique de
 * 2 participations d'un SIRET sur une même campagne).
 */
function ParticipationsTable({
  rows,
}: ParticipationsTableProps): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Chronologie des participations du fournisseur aux campagnes de
          référencement (campagne, état de campagne, état de participation,
          cycle, date), de la plus récente à la plus ancienne
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            <th scope="col" className="px-4 py-3 font-medium">
              Campagne
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              État campagne
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              État participation
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Cycle
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Date
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const campaign = campaignStateLabel(row.campaignState);
            const participation = participationStateLabel(
              row.participationState,
            );
            return (
              <tr
                key={row.participationId}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-4 py-3 align-top">{row.intitule}</td>
                <td className="px-4 py-3 align-top">
                  <Badge variant={campaign.variant}>{campaign.label}</Badge>
                </td>
                <td className="px-4 py-3 align-top">
                  <Badge variant={participation.variant}>
                    {participation.label}
                  </Badge>
                </td>
                <td className="px-4 py-3 align-top">
                  {row.currentCycle === null
                    ? '—'
                    : cycleNumberLabel(row.currentCycle)}
                </td>
                <td className="px-4 py-3 align-top">
                  {formatParticipationDate(row.lastRelevantAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// --- Section 5 — Commandes admin (transitions + invitation acte 2) ---
//
// T3-SUSPENSION (REF 1.7 §5, DBU §2.2 l.702) : Suspendre et Refuser exigent la SAISIE D'UN MOTIF
// (`reason`, texte libre OBLIGATOIRE). Le motif part dans le corps ; le SIRET reste la ressource d'URL
// (invariant n°1) ; `adminId`/IP sont posés SERVEUR. Réactiver/Archiver restent SANS motif (schémas
// contrat `.strict()`). Frontières NETTES : les actions contact (révoquer/transférer/renvoyer) vivent
// dans `InterlocuteursSection`, la relance CGU (T5) dans la section Identité, les tokens (T6) dans
// `AccessTokensSection` — cette section ne touche QUE les transitions d'établissement.

/**
 * Traduit une erreur de transition en message utilisateur PAR CODE (jamais le message serveur brut).
 * 409 = état concurrent modifié (transition illégale sur l'état courant relu) ; 400 = motif manquant
 * (garde serveur `min(1)`, normalement pré-bloquée côté UI). Parité `relanceErrorMessage`.
 */
function transitionErrorMessage(error: unknown): string {
  if (error instanceof SupplierApiError) {
    if (error.status === 409) {
      return "L'état du fournisseur a changé. Rechargez la fiche avant de réessayer.";
    }
    if (error.status === 400) {
      return 'Un motif est requis pour cette action.';
    }
  }
  return "L'action n'a pas pu être effectuée. Réessayez plus tard.";
}

/**
 * Champ « motif » OBLIGATOIRE d'un dialog de transition (Suspendre/Refuser). Textarea labellisée
 * (WCAG 1.3.1/4.1.2 : `htmlFor`/`id` natif), `required` + `aria-required`, focus déplacé sur le champ à
 * l'ouverture (le bouton Confirmer est désactivé tant que le motif est vide → il ne capte pas le focus).
 */
function ReasonField({
  id,
  label,
  value,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): React.JSX.Element {
  const ref = React.useRef<HTMLTextAreaElement | null>(null);
  React.useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <textarea
        id={id}
        ref={ref}
        value={value}
        required
        aria-required="true"
        rows={3}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-[6rem] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      />
    </div>
  );
}

interface CommandsSectionProps {
  readonly siret: string;
  readonly state: AdminEstablishmentDto['etablissementState'];
}

function CommandsSection({
  siret,
  state,
}: CommandsSectionProps): React.JSX.Element {
  const sendInvitation = useSendInvitation();
  const refuse = useRefuseEstablishment();
  const suspend = useSuspendEstablishment();
  const reactivate = useReactivateEstablishment();
  const archive = useArchiveEstablishment();

  // Un seul dialog ouvert à la fois → le motif (`reason`) est un état partagé, réinitialisé à la
  // fermeture. `feedback` porte l'erreur PAR CODE de la dernière transition (role=alert).
  const [confirm, setConfirm] = React.useState<
    null | 'suspend' | 'archive' | 'refuse'
  >(null);
  const [reason, setReason] = React.useState('');
  const [feedback, setFeedback] = React.useState<string | null>(null);

  const openDialog = (kind: 'suspend' | 'archive' | 'refuse'): void => {
    setFeedback(null);
    setReason('');
    setConfirm(kind);
  };
  const closeDialog = (): void => {
    setConfirm(null);
    setReason('');
  };
  const onTransitionError = (error: unknown): void => {
    setFeedback(transitionErrorMessage(error));
  };

  const reasonEmpty = reason.trim() === '';

  return (
    <section aria-labelledby="commands-heading" className="space-y-4">
      <h2 id="commands-heading" className="text-lg font-medium">
        Actions administrateur
      </h2>
      <Card>
        <CardContent className="flex flex-wrap gap-3 py-6">
          {/* Acte 2 du flux A-05 : envoyer l'invitation (Brouillon→Soumis). */}
          {state === 'Brouillon' ? (
            <Button
              type="button"
              onClick={() =>
                sendInvitation.mutate({ siret, body: {} })
              }
              disabled={sendInvitation.isPending}
            >
              Envoyer l'invitation
            </Button>
          ) : null}

          {/* Refuser (Soumis→Refuse) : MOTIF OBLIGATOIRE → dialog de confirmation (T3-SUSPENSION). */}
          {state === 'Soumis' ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => openDialog('refuse')}
              disabled={refuse.isPending}
            >
              Refuser
            </Button>
          ) : null}

          {state === 'Actif' ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => openDialog('suspend')}
            >
              Suspendre
            </Button>
          ) : null}

          {state === 'Suspendu' ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                reactivate.mutate(siret, { onError: onTransitionError })
              }
              disabled={reactivate.isPending}
            >
              Réactiver
            </Button>
          ) : null}

          {state !== 'Archive' ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => openDialog('archive')}
            >
              Archiver
            </Button>
          ) : null}
        </CardContent>
      </Card>

      {feedback !== null ? (
        <p role="alert" className="text-sm text-destructive">
          {feedback}
        </p>
      ) : null}

      {/* Suspension (Actif→Suspendu) — DESTRUCTIVE + MOTIF OBLIGATOIRE (Confirmer bloqué si vide). */}
      <ConfirmDialog
        open={confirm === 'suspend'}
        title="Suspendre ce fournisseur ?"
        description="Le fournisseur ne pourra plus opérer tant qu'il n'est pas réactivé. Cette action est tracée."
        confirmLabel="Suspendre"
        confirmDisabled={reasonEmpty}
        onConfirm={() => {
          suspend.mutate({ siret, reason: reason.trim() }, { onError: onTransitionError });
          closeDialog();
        }}
        onCancel={closeDialog}
      >
        <ReasonField
          id="suspend-reason"
          label="Motif de suspension"
          value={reason}
          onChange={setReason}
        />
      </ConfirmDialog>

      {/* Refus (Soumis→Refuse) — MOTIF OBLIGATOIRE (Confirmer bloqué si vide). */}
      <ConfirmDialog
        open={confirm === 'refuse'}
        title="Refuser ce fournisseur ?"
        description="Le fournisseur sera refusé et ne pourra pas être activé. Cette action est tracée."
        confirmLabel="Refuser"
        confirmDisabled={reasonEmpty}
        onConfirm={() => {
          refuse.mutate({ siret, reason: reason.trim() }, { onError: onTransitionError });
          closeDialog();
        }}
        onCancel={closeDialog}
      >
        <ReasonField
          id="refuse-reason"
          label="Motif de refus"
          value={reason}
          onChange={setReason}
        />
      </ConfirmDialog>

      {/* Archivage (no-DELETE) — confirmation explicite requise (DBU l.979), SANS motif. */}
      <ConfirmDialog
        open={confirm === 'archive'}
        title="Archiver ce fournisseur ?"
        description="L'établissement sera archivé (aucune suppression de données). Cette action est tracée."
        confirmLabel="Archiver"
        onConfirm={() => {
          archive.mutate(siret, { onError: onTransitionError });
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
    </section>
  );
}
